/** @file The two-point quad: pivot2-edge corners from B', the others from A', all turned (design 21.6). */
import type { PerCorner } from '@zilo/pose3d-render-core/contract';
import { rotateLocal } from '@zilo/pose3d-render-core/corners/rotateLocal';
import type { Pivot2 } from '@zilo/pose3d-render-core/cut/pivot2Of';
import { TILE_EDGES } from '@zilo/pose3d-render-core/cut/tileEdges';

/**
 * Corners `[tl, tr, br, bl]` of the `w x h` tile: a corner on pivot2's edge is `B' + rotate(q - pivot2, theta)`, every
 * other corner `A' + rotate(q - pivot, theta)`, so the image stretches between the two points with no scale factor
 * (both pairs share the edge vector: the result is a parallelogram).
 */
export function twoPointQuadOf(aScreen: [number, number], bScreen: [number, number], pivot: [number, number], pivot2: Pivot2, size: [number, number], theta: number): PerCorner<[number, number]> {
  const [w, h] = size;
  const onEdge = TILE_EDGES[pivot2.edge].corners;
  const local: PerCorner<[number, number]> = [[0, 0], [w, 0], [w, h], [0, h]];
  return local.map((q, i): [number, number] => {
    const [origin, from] = onEdge.includes(i) ? [bScreen, pivot2.point] : [aScreen, pivot];
    const r = rotateLocal(q[0] - from[0], q[1] - from[1], theta);
    return [origin[0] + r[0], origin[1] + r[1]];
  }) as PerCorner<[number, number]>;
}
